import { Capacitor } from '@capacitor/core';
import { hashPhone, getCountryFromPhone } from '../utils/phoneHash';
import type { CountryCode } from '../utils/phoneHash';
import * as sb from './supabaseService';

export interface ImportedContact {
  name: string;
  phone: string; // raw phone string from device contacts
}

/**
 * Keep raw phone string as-is from contacts. Normalization to E.164 happens
 * at hash time (in storeContacts) using the importing user's country as default.
 */
function cleanPhone(phone: string): string {
  return phone.replace(/[^\d+\s()-]/g, '').trim();
}

export async function hasSharedContacts(userId: string): Promise<boolean> {
  return sb.hasImportedContacts(userId);
}

export async function pickContacts(): Promise<ImportedContact[] | null> {
  if (Capacitor.isNativePlatform()) {
    return pickContactsNative();
  }
  return pickContactsWeb();
}

async function pickContactsNative(): Promise<ImportedContact[] | null> {
  try {
    const { CapacitorContacts } = await import('@capgo/capacitor-contacts');
    const permission = await CapacitorContacts.requestPermissions();
    if (permission.readContacts !== 'granted') return null;

    const result = await CapacitorContacts.getContacts({
      fields: ['fullName', 'givenName', 'familyName', 'phoneNumbers'],
    });

    const imported: ImportedContact[] = [];
    for (const contact of result.contacts) {
      const name = contact.fullName || [contact.givenName, contact.familyName].filter(Boolean).join(' ') || 'Unknown';
      const phones = contact.phoneNumbers ?? [];
      if (phones.length > 0 && phones[0].value) {
        imported.push({
          name,
          phone: cleanPhone(phones[0].value),
        });
      }
    }

    return imported.length > 0 ? imported : null;
  } catch {
    return null;
  }
}

async function pickContactsWeb(): Promise<ImportedContact[] | null> {
  if (!('contacts' in navigator && 'ContactsManager' in window)) {
    return null;
  }
  try {
    const nav = navigator as any;
    const contacts = await nav.contacts.select(['name', 'tel'], { multiple: true });
    if (!contacts || contacts.length === 0) return null;

    const imported: ImportedContact[] = [];
    for (const contact of contacts) {
      const name = contact.name?.[0] || 'Unknown';
      const phones: string[] = contact.tel ?? [];
      if (phones.length > 0) {
        imported.push({ name, phone: cleanPhone(phones[0]) });
      }
    }

    return imported.length > 0 ? imported : null;
  } catch {
    return null;
  }
}

export function parseManualPhones(input: string): ImportedContact[] {
  const lines = input.split(/[,\n]+/).map((s) => s.trim()).filter(Boolean);
  return lines
    .map((raw, i) => ({
      name: `Contact ${i + 1}`,
      phone: cleanPhone(raw),
    }))
    .filter((c) => c.phone.replace(/\D/g, '').length >= 7);
}

/**
 * Hash and store imported contacts in Supabase for matching.
 *
 * Uses the importing user's phone number to determine default country.
 * This way "050 123 4567" from a UAE user normalizes to "+971501234567"
 * before hashing — matching the profile hash of that phone's owner.
 *
 * Only SHA-256 hashes are sent to the server — no raw phone numbers.
 */
export async function storeContacts(
  userId: string,
  contacts: ImportedContact[],
  userPhone?: string
): Promise<void> {
  const defaultCountry: CountryCode = userPhone
    ? getCountryFromPhone(userPhone)
    : 'AE';

  const hashed = await Promise.all(
    contacts.map(async (c) => ({
      phone_hash: await hashPhone(c.phone, defaultCountry),
    }))
  );

  await sb.storeImportedContacts(userId, hashed);
}
